import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuthState } from "../../provider/AuthProvider";
import type { ColumnsType, SorterResult } from "antd/es/table/interface";
import { TableProps } from "antd/es/table/InternalTable";

import * as TypeDTO from "../../commons/typeDTO";
import * as TypeUtils from "../../commons/typeUtils";
import * as Common from "../../commons/common";
import * as String from "../../commons/string";
import * as Utils from "../../utils/utils";
import * as RequestRunning from "../../utils/requestApiRunning";

import Badge from "antd/es/badge/index";
import Space from "antd/es/space/index";
import Tag from "antd/es/tag/index";

import moment from "moment";
import DataTable from "../../components/common/DataTable";
import Filter from "../../components/mobile/Filter";

type SearchType = {
    carNumber: string;
    makerName: string;
    categoryName: string;
    startDate: string;
    endDate: string;
    checkbox: string[];
};

const PageRunnings = () => {
    const navigate = useNavigate();
    const userDetails = useAuthState();

    const [sortedInfo, setSortedInfo] = useState<SorterResult<TypeDTO.RunningHistoryDto>>({});
    const [searchHistoryValue, setSearchHistoryValue] = useState<SearchType>();
    const [loadingHistories, setLoadingHistories] = useState(false);
    const [tempHistories, setTempHistories] = useState<Array<TypeDTO.RunningHistoryDto>>();
    const [requestHistoriesPage, setRequestHistoriesPage] = useState(0);
    const [histories, setHistories] = useState<Array<TypeDTO.RunningHistoryDto>>();
    const [filteredHistories, setFilteredHistories] = useState<Array<TypeDTO.RunningHistoryDto>>();

    const columns: ColumnsType<TypeDTO.RunningHistoryDto> = [
        {
            title: String.runningDate,
            dataIndex: "startTime",
            key: "startTime",
            width: "10%",
            sorter: (a, b, sortOrder) => Utils.sortDate(a.startTime, b.startTime, sortOrder),
            sortOrder: sortedInfo.columnKey === "time" ? sortedInfo.order : null,
            render: (_, { startTime }) => moment.utc(startTime, Common.FORMAT_DATE).format(Common.FORMAT_DATE),
        },
        {
            title: String.carNumber,
            dataIndex: ["car", "carNumber"],
            key: "carNumber",
            width: "10%",
            align: "center",
        },
        {
            title: String.error + "/" + String.warning,
            key: "alarm",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.errorCount, b.errorCount, sortOrder),
            sortOrder: sortedInfo.columnKey === "errorCount" ? sortedInfo.order : null,
            render: (_, row) =>
                row.errorCount || row.warningCount ? (
                    <Space size={0}>
                        {row.errorCount ? <Tag className="errorTag">{row.errorCount}</Tag> : ""}
                        {row.warningCount ? <Tag className="warningTag">{row.warningCount}</Tag> : ""}
                    </Space>
                ) : (
                    <Tag className="normalTag">{row.errorCount}</Tag>
                ),
        },
        {
            title: String.startTime,
            dataIndex: "startTime",
            key: "startTime",
            align: "center",
            width: "8%",
            render: (_, { startTime, rtcCheck }) => {
                return rtcCheck === true ? (
                    <Badge style={{ right: "-4px" }} dot color="var(--danger)">
                        <span className="fs-md">{startTime ? moment(startTime).format(Common.FORMAT_TIME) : String.dash}</span>
                    </Badge>
                ) : startTime ? (
                    <span className="fs-md">{moment(startTime).format(Common.FORMAT_TIME)}</span>
                ) : (
                    String.dash
                );
            },
        },
        {
            title: String.endTime,
            dataIndex: "endTime",
            align: "center",
            width: "8%",
            render: (_, { startTime, endTime, finish }) => {
                const checkDday = moment(endTime).startOf("day").diff(moment(startTime).startOf("day"), "days");
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {checkDday > 0 && (
                            <Tag className="normalTag" style={{ marginRight: 4 }}>
                                D+{checkDday}
                            </Tag>
                        )}
                        {endTime ? (
                            finish === true ? (
                                <span className="fs-md">{endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}</span>
                            ) : Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN]) ? (
                                <Badge style={{ right: "-4px" }} dot color="var(--danger)">
                                    <span className="fc-point fs-md">
                                        {endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}
                                    </span>
                                </Badge>
                            ) : (
                                <span className="fs-md">{endTime ? moment(endTime).format(Common.FORMAT_MOBILE_DATE_TIME) : String.dash}</span>
                            )
                        ) : (
                            <span className="fs-md">{String.dash}</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: String.runningTime,
            dataIndex: "drivingTime",
            key: "drivingTime",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.drivingTime, b.drivingTime, sortOrder),
            sortOrder: sortedInfo.columnKey === "drivingTime" ? sortedInfo.order : null,
            render: (_, { drivingTime }) => Utils.secToTime(Number(drivingTime)),
        },
        {
            title: (
                <>
                    {String.traveledDistance}
                    <span className="fs-xs"> {String.kmDistanceUnit}</span>
                </>
            ),
            dataIndex: "drivingDistance",
            key: "drivingDistance",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.drivingDistance, b.drivingDistance, sortOrder),
            sortOrder: sortedInfo.columnKey === "drivingDistance" ? sortedInfo.order : null,
            render: (_, { drivingDistance }) => Utils.checkNumberData(drivingDistance),
        },
        {
            title: (
                <>
                    {String.avgSpeed}
                    <span className="fs-xs"> {String.kmSpeedUnit}</span>
                </>
            ),
            dataIndex: "avgSpeed",
            key: "avgSpeed",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.avgSpeed, b.avgSpeed, sortOrder),
            sortOrder: sortedInfo.columnKey === "avgSpeed" ? sortedInfo.order : null,
            render: (_, { avgSpeed }) => Utils.checkNumberData(avgSpeed),
        },
        {
            title: (
                <>
                    {String.electricMileage}
                    <span className="fs-xs"> {String.kmElectricMileageUnit}</span>
                </>
            ),
            dataIndex: "electricMileage",
            key: "electricMileage",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortNumber(a.electricMileage, b.electricMileage, sortOrder),
            sortOrder: sortedInfo.columnKey === "electricMileage" ? sortedInfo.order : null,
            render: (_, { electricMileage }) => Utils.checkNumberData(electricMileage),
        },
    ];

    const onTableChange: TableProps<TypeDTO.RunningHistoryDto>["onChange"] = (pagination, filters, sorter) => {
        setSortedInfo(sorter as SorterResult<TypeDTO.RunningHistoryDto>);
    };

    const search: TypeUtils.SearchItemType[] = [
        {
            id: "carNumber",
            span: 12,
            title: String.carNumber,
            type: "select",
            typeDetail: "car",
        },
        {
            id: "makerName",
            span: 6,
            title: String.maker,
            type: "select",
            typeDetail: "maker",
        },
        {
            id: "categoryName",
            span: 6,
            title: String.category,
            type: "select",
            typeDetail: "category",
        },
        {
            id: "searchDate",
            span: 12,
            title: String.searchDate,
            type: "rangePicker",
            dateFormat: Common.FORMAT_DATE_TIME,
        },
        {
            id: "checkbox",
            span: 12,
            title: String.alarmStatus,
            type: "checkbox",
            options: [
                { value: "error", label: String.error },
                { value: "warning", label: String.warning },
                { value: "normal", label: String.normal },
            ],
            defaultValue: ["error", "warning", "normal"],
        },
    ];

    const onSearchData = (value: any) => {
        const { checkbox, ...searchValue } = value;

        searchValue.error = value?.error ?? value?.checkbox?.includes("error") ?? false;
        searchValue.warning = value?.warning ?? value?.checkbox?.includes("warning") ?? false;
        searchValue.normal = value?.normal ?? value?.checkbox?.includes("normal") ?? false;

        setSearchHistoryValue(searchValue);
        setLoadingHistories(true);
        requestApiRunningHistoryList(searchValue, requestHistoriesPage);
    };

    const { requestApiRunningHistoryList, resultApiRunningHistoryList } = RequestRunning.useRequestApiRunningHistoryList();

    useEffect(() => {
        if (!resultApiRunningHistoryList) return;

        if (resultApiRunningHistoryList.runningHistories.totalPages > requestHistoriesPage + 1) {
            setTempHistories([...(tempHistories || []), ...resultApiRunningHistoryList.runningHistories.content]);
            setRequestHistoriesPage(requestHistoriesPage + 1);
        } else {
            setLoadingHistories(false);
            setHistories([...(tempHistories || []), ...resultApiRunningHistoryList.runningHistories.content]);
            setRequestHistoriesPage(0);
            setTempHistories([]);
        }
    }, [resultApiRunningHistoryList]);

    useEffect(() => {
        if (requestHistoriesPage === 0) return;

        requestApiRunningHistoryList(searchHistoryValue, requestHistoriesPage);
    }, [requestHistoriesPage]);

    useEffect(() => {
        if (histories === undefined) return;
        const array: Array<TypeDTO.RunningHistoryDto> = [];
        histories.map((history) => {
            history.rtcCheck !== true && array.push(history);
        });
        setFilteredHistories(array);
    }, [histories]);

    return (
        <div className="pageWrapper">
            <Filter dataLength={histories?.length} values={search} onSearch={(value) => onSearchData(value)} />
            <DataTable
                isMobile
                rowKey="endTime"
                disabledTitle
                columns={columns}
                dataSource={Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN]) ? histories : filteredHistories}
                onRow={(value: TypeDTO.RunningHistoryDto) => navigate(Common.PAGE_RUNNING_DETAILS + "/" + value.car.carId + "/" + value.bootTime)}
                onChange={onTableChange}
            />
        </div>
    );
};

export default PageRunnings;
